'use client';

import { DEFAULT_SYSTEM_LANGUAGE } from '@/lib/constants';
import { EQueryKeys } from '@/lib/enums';
import { getBootApiRoute, processBootData } from '@/lib/sharedBootUtils';
import { PortalBootResponse } from '@/lib/types';
import { useAppState } from '@/state/hooks/app.state.hook';
import { useQuery, UseQueryResult } from '@tanstack/react-query';

interface IUsePortalBootReturn {
  getPortalBoot: UseQueryResult<PortalBootResponse, Error>;
}

export const usePortalBoot = (): IUsePortalBootReturn => {
  const { currentLanguage, domain, initialBoot } = useAppState();
  const language = currentLanguage || DEFAULT_SYSTEM_LANGUAGE;
  const reusableInitialBoot =
    initialBoot?.locale === language && initialBoot.isFallbackBoot !== true
      ? initialBoot
      : undefined;

  const getPortalBoot = useQuery<PortalBootResponse, Error>({
    queryKey: [EQueryKeys.PORTAL_BOOT, language, domain],
    initialData: reusableInitialBoot,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      try {
        const currentDomain = domain || 'rony-princess-portal.appchargestore.com';
        const route = getBootApiRoute(currentDomain, language);

        const fetchInitialBoot = await fetch(route);
        const bootJson = await fetchInitialBoot.json();
        const processedBootData = processBootData(bootJson);

        return processedBootData;
      } catch (e) {
        // Rethrown rather than answered with a stub. Both readers of this query
        // ask for `data?.pages || initialBoot.pages`, so leaving `data` unset
        // hands them the boot the server already fetched and put in the page —
        // real config for this portal. A stub is not, and being truthy it took
        // that fallback away, which is how a dropped request became a portal
        // with no pages.
        console.error('🔄 [usePortalBoot] Portal boot is failing:', e);

        throw e;
      }
    },
    enabled: !!language && !!domain
  });

  return {
    getPortalBoot
  };
};
